import React from "react";

export type CardBrandKey =
  | "visa"
  | "mastercard"
  | "amex"
  | "discover"
  | "diners"
  | "jcb"
  | "unionpay"
  | "bacs"
  | "unknown";

/**
 * Normalises a backend/Stripe-supplied brand string (e.g. "visa", "MasterCard",
 * "Master", "American Express", "BACS", null) into a canonical key used to
 * pick the correct logo. Case-, space- and hyphen-insensitive.
 */
export function normaliseCardBrand(rawBrand: string | null | undefined): CardBrandKey {
  if (!rawBrand) {
    return "unknown";
  }

  const key = rawBrand.toLowerCase().replace(/[\s\-_]/g, "");

  if (key === "visa") return "visa";
  if (key === "mastercard" || key === "master" || key === "mc") return "mastercard";
  if (key === "amex" || key === "americanexpress") return "amex";
  if (key === "discover") return "discover";
  if (key === "diners" || key === "dinersclub") return "diners";
  if (key === "jcb") return "jcb";
  if (key === "unionpay" || key === "cup") return "unionpay";
  if (key === "bacs") return "bacs";
  return "unknown";
}

interface CardBrandLogoProps {
  brand: string | null | undefined;
  className?: string;
  title?: string;
}

/**
 * Renders a recognisable inline-SVG logo for the supplied card brand.
 * Falls back to a neutral "credit card" glyph for unknown / missing brands.
 * The viewBox is a consistent 96x60 so callers can size the logo via `className`
 * (width/height) without worrying about per-brand dimensions.
 */
function CardBrandLogo({ brand, className, title }: CardBrandLogoProps): React.ReactElement {
  const key = normaliseCardBrand(brand);
  const label = title ?? readableLabel(key);

  return (
    <svg
      viewBox="0 0 96 60"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {renderLogo(key)}
    </svg>
  );
}

function readableLabel(key: CardBrandKey): string {
  switch (key) {
    case "visa": return "Visa";
    case "mastercard": return "Mastercard";
    case "amex": return "American Express";
    case "discover": return "Discover";
    case "diners": return "Diners Club";
    case "jcb": return "JCB";
    case "unionpay": return "UnionPay";
    case "bacs": return "BACS Direct Debit";
    default: return "Card";
  }
}

function renderLogo(key: CardBrandKey): React.ReactNode {
  switch (key) {
    case "visa": return <VisaLogo />;
    case "mastercard": return <MastercardLogo />;
    case "amex": return <AmexLogo />;
    case "discover": return <DiscoverLogo />;
    case "diners": return <DinersLogo />;
    case "jcb": return <JcbLogo />;
    case "unionpay": return <UnionPayLogo />;
    case "bacs": return <BacsLogo />;
    default: return <GenericCardLogo />;
  }
}

function VisaLogo(): React.ReactElement {
  return (
    <g>
      <rect x="0" y="0" width="96" height="60" rx="6" fill="#ffffff" stroke="#e6e9ef" />
      <text
        x="48"
        y="40"
        textAnchor="middle"
        fontFamily="'Helvetica Neue', Arial, sans-serif"
        fontWeight={900}
        fontStyle="italic"
        fontSize="22"
        fill="#1a1f71"
        letterSpacing="1.5"
      >
        VISA
      </text>
    </g>
  );
}

function MastercardLogo(): React.ReactElement {
  return (
    <g>
      <rect x="0" y="0" width="96" height="60" rx="6" fill="#ffffff" stroke="#e6e9ef" />
      <mask id="mc-intersect">
        <rect width="96" height="60" fill="black" />
        <circle cx="36" cy="30" r="16" fill="white" />
      </mask>
      <circle cx="36" cy="30" r="16" fill="#eb001b" />
      <circle cx="56" cy="30" r="16" fill="#f79e1b" />
      <circle cx="56" cy="30" r="16" fill="#ff5f00" mask="url(#mc-intersect)" />
      <text
        x="48"
        y="53"
        textAnchor="middle"
        fontFamily="'Helvetica Neue', Arial, sans-serif"
        fontWeight={700}
        fontSize="7"
        fill="#1a1a1a"
        letterSpacing="0.5"
      >
        mastercard
      </text>
    </g>
  );
}

function AmexLogo(): React.ReactElement {
  return (
    <g>
      <rect x="0" y="0" width="96" height="60" rx="6" fill="#006fcf" />
      <text
        x="48"
        y="28"
        textAnchor="middle"
        fontFamily="'Helvetica Neue', Arial, sans-serif"
        fontWeight={800}
        fontSize="10"
        fill="#ffffff"
        letterSpacing="1"
      >
        AMERICAN
      </text>
      <text
        x="48"
        y="42"
        textAnchor="middle"
        fontFamily="'Helvetica Neue', Arial, sans-serif"
        fontWeight={800}
        fontSize="10"
        fill="#ffffff"
        letterSpacing="1"
      >
        EXPRESS
      </text>
    </g>
  );
}

function DiscoverLogo(): React.ReactElement {
  return (
    <g>
      <rect x="0" y="0" width="96" height="60" rx="6" fill="#ffffff" stroke="#e6e9ef" />
      <circle cx="72" cy="40" r="16" fill="#ff6a00" />
      <text
        x="48"
        y="34"
        textAnchor="middle"
        fontFamily="'Helvetica Neue', Arial, sans-serif"
        fontWeight={900}
        fontSize="11"
        fill="#1a1a1a"
        letterSpacing="0.5"
      >
        DISCOVER
      </text>
    </g>
  );
}

function DinersLogo(): React.ReactElement {
  return (
    <g>
      <rect x="0" y="0" width="96" height="60" rx="6" fill="#ffffff" stroke="#e6e9ef" />
      <circle cx="38" cy="30" r="14" fill="#0079be" />
      <circle cx="58" cy="30" r="14" fill="#0079be" />
      <circle cx="48" cy="30" r="6" fill="#ffffff" />
      <text
        x="48"
        y="53"
        textAnchor="middle"
        fontFamily="'Helvetica Neue', Arial, sans-serif"
        fontWeight={700}
        fontSize="7"
        fill="#0079be"
        letterSpacing="0.4"
      >
        diners club
      </text>
    </g>
  );
}

function JcbLogo(): React.ReactElement {
  return (
    <g>
      <rect x="0" y="0" width="96" height="60" rx="6" fill="#ffffff" stroke="#e6e9ef" />
      <rect x="16" y="18" width="20" height="28" rx="3" fill="#0a478f" />
      <rect x="38" y="18" width="20" height="28" rx="3" fill="#ba1024" />
      <rect x="60" y="18" width="20" height="28" rx="3" fill="#067d41" />
      <text
        x="48"
        y="37"
        textAnchor="middle"
        fontFamily="'Helvetica Neue', Arial, sans-serif"
        fontWeight={800}
        fontSize="11"
        fill="#ffffff"
        letterSpacing="0.5"
      >
        JCB
      </text>
    </g>
  );
}

function UnionPayLogo(): React.ReactElement {
  return (
    <g>
      <rect x="0" y="0" width="96" height="60" rx="6" fill="#ffffff" stroke="#e6e9ef" />
      <text
        x="48"
        y="38"
        textAnchor="middle"
        fontFamily="'Helvetica Neue', Arial, sans-serif"
        fontWeight={800}
        fontSize="13"
        fill="#e21836"
        letterSpacing="0.5"
      >
        UnionPay
      </text>
    </g>
  );
}

function BacsLogo(): React.ReactElement {
  return (
    <g>
      <rect x="0" y="0" width="96" height="60" rx="6" fill="#f6f8fb" stroke="#e6e9ef" />
      <path
        d="M16 26 L48 14 L80 26 L80 30 L16 30 Z M20 34 H28 V46 H20 Z M44 34 H52 V46 H44 Z M68 34 H76 V46 H68 Z M16 48 H80 V52 H16 Z"
        fill="#004b4d"
      />
      <text
        x="48"
        y="58"
        textAnchor="middle"
        fontFamily="'Helvetica Neue', Arial, sans-serif"
        fontWeight={700}
        fontSize="6"
        fill="#004b4d"
        letterSpacing="0.4"
      >
        BACS
      </text>
    </g>
  );
}

function GenericCardLogo(): React.ReactElement {
  return (
    <g>
      <rect x="2" y="6" width="92" height="48" rx="6" fill="#ffffff" stroke="#cfd4dd" strokeWidth={1.5} />
      <rect x="2" y="16" width="92" height="8" fill="#1a1f71" />
      <rect x="12" y="34" width="22" height="14" rx="2" fill="#ffcc66" stroke="#b8860b" />
      <line x1="14" y1="38" x2="32" y2="38" stroke="#b8860b" />
      <line x1="14" y1="42" x2="32" y2="42" stroke="#b8860b" />
    </g>
  );
}

export default CardBrandLogo;
